import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, MapPin, Calendar, Clock, DollarSign, Loader2 } from 'lucide-react';
import { formatTime12h } from '../../utils/timeFormat';
import OneTapApplyButton from '../../components/OneTapApplyButton';
import CertificationPromptCard from '../../components/CertificationPromptCard';

/**
 * Iter-173 — One-Tap Apply landing page.
 *
 * Mobile-first deep-link target for SMS / email / push notifications.
 * Route: /talent/apply/:shiftId  (optional ?auto=1 for instant apply)
 *
 * Renders a minimal shift summary + the OneTapApplyButton so the talent
 * can apply in a single tap. Fully reuses the OneTapApplyButton state
 * machine for eligibility + blockers + auto-apply.
 */
const TalentOneTapApply = () => {
  const { shiftId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const autoApply = params.get('auto') === '1';

  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msgToClient, setMsgToClient] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/talent/one-tap-apply/${shiftId}/eligibility`);
        if (!cancelled) setShift(r.data?.shift || null);
      } catch (e) { /* handled by button */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [shiftId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-emerald-50" data-testid="one-tap-apply-page">
      <header className="bg-white/80 backdrop-blur border-b border-gray-200">
        <div className="max-w-xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-lg font-bold">Apply to Gig</h1>
        </div>
      </header>

      <div className="max-w-xl mx-auto px-4 py-6 space-y-4">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
          </div>
        ) : !shift ? (
          <Card>
            <CardContent className="py-10 text-center text-gray-500" data-testid="shift-unavailable">
              This gig is no longer available.
            </CardContent>
          </Card>
        ) : (
          <>
            <Card data-testid="shift-summary-card">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    {/* Iter-182 — public-facing title is now the headline */}
                    <h2 className="text-xl font-bold text-gray-900 leading-tight" data-testid="apply-event-title">
                      {shift.event_title || `${shift.role_name} Gig`}
                    </h2>
                    <p className="text-xs uppercase tracking-wide text-cyan-600 mt-1" data-testid="apply-role-name">
                      {shift.role_name} needed
                    </p>
                    {shift.venue_name && (
                      <p className="text-sm text-gray-500 truncate mt-1" data-testid="apply-venue-name">{shift.venue_name}</p>
                    )}
                  </div>
                  {shift.spots_remaining > 0 ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-0 shrink-0">
                      {shift.spots_remaining} spot{shift.spots_remaining === 1 ? '' : 's'} open
                    </Badge>
                  ) : (
                    <Badge className="bg-rose-100 text-rose-700 border-0 shrink-0">Filled</Badge>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Calendar className="w-4 h-4 text-cyan-500 shrink-0" />
                    {shift.shift_date}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Clock className="w-4 h-4 text-cyan-500 shrink-0" />
                    {formatTime12h(shift.start_time)} – {formatTime12h(shift.end_time)}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <MapPin className="w-4 h-4 text-cyan-500 shrink-0" />
                    {shift.city}, {shift.state}
                    {shift.distance_miles != null && (
                      <span className="text-xs text-gray-400">· {shift.distance_miles}mi</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <DollarSign className="w-4 h-4 text-emerald-500 shrink-0" />
                    {shift.hourly_rate ? `$${shift.hourly_rate}/hr` : 'Rate TBD'}
                  </div>
                </div>

                {/* Iter-182 — Application Context: duties, dress code, notes shown
                    before final apply so talent know exactly what they're applying for. */}
                {(shift.duties || shift.dress_code_name || shift.dress_code_description || shift.additional_notes) && (
                  <div className="border-t pt-3 space-y-3 mt-2" data-testid="application-context">
                    {shift.duties && (
                      <div data-testid="apply-duties-block">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Duties</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap mt-0.5">{shift.duties}</p>
                      </div>
                    )}
                    {(shift.dress_code_name || shift.dress_code_description) && (
                      <div data-testid="apply-dress-code-block">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Dress Code</p>
                        <p className="text-sm text-gray-700 mt-0.5">
                          {shift.dress_code_name && <span className="font-medium">{shift.dress_code_name}</span>}
                          {shift.dress_code_name && shift.dress_code_description && <span> — </span>}
                          {shift.dress_code_description}
                        </p>
                      </div>
                    )}
                    {shift.additional_notes && (
                      <div data-testid="apply-notes-block">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Important Notes</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap mt-0.5">{shift.additional_notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="px-1">
              {/* Iter-225d / Phase 1C-A — non-blocking cert prompt above the apply CTA.
                  Task-4 also wires the inline Guardian "Take Assessment" CTA
                  through this component when the shift is a Guardian gig. */}
              <CertificationPromptCard shiftId={shiftId} className="mb-3" />

              {/* Task-3 — Message to Client (optional, <=2000 chars) */}
              <div className="mb-3" data-testid="apply-message-to-client-block">
                <label
                  htmlFor="apply-message-to-client"
                  className="text-xs font-semibold uppercase tracking-wide text-gray-500 flex justify-between mb-1"
                >
                  <span>Message to Client <span className="text-gray-400 font-normal normal-case">(optional)</span></span>
                  <span className="text-gray-400 font-normal">{msgToClient.length}/2000</span>
                </label>
                <textarea
                  id="apply-message-to-client"
                  data-testid="apply-message-to-client-input"
                  rows={3}
                  maxLength={2000}
                  value={msgToClient}
                  onChange={(e) => setMsgToClient(e.target.value)}
                  placeholder="Introduce yourself, mention relevant experience, or note anything the client should know."
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-cyan-400"
                />
              </div>

              <OneTapApplyButton
                shiftId={shiftId}
                autoApply={autoApply}
                message={msgToClient}
                onApplied={() => {/* stay on page so confirmation toast shows */}}
                className="w-full text-base py-6"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TalentOneTapApply;
